import { useState, FormEvent } from 'react';

export default function RequestForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    equipmentType: '',
    serviceLocation: '',
    issueDescription: '',
    contactMethod: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate textarea length
    if (formData.issueDescription.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('equipmentType', formData.equipmentType);
      formBody.append('serviceLocation', formData.serviceLocation);
      formBody.append('issueDescription', formData.issueDescription);
      formBody.append('contactMethod', formData.contactMethod);

      const response = await fetch('https://readdy.ai/api/form/d6m87rlv117fnkj2h2p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          equipmentType: '',
          serviceLocation: '',
          issueDescription: '',
          contactMethod: 'phone'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="request-form" className="py-20 bg-gradient-to-r from-[#14B8A6] to-[#0d9488]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* Left Side - Info */}
          <div className="lg:col-span-2">
            <h2 className="text-4xl font-bold text-white mb-6">
              Need Repair Service?
            </h2>
            <p className="text-white text-lg mb-8 leading-relaxed">
              Fill out our quick form and we'll contact you within 2 hours during business hours
            </p>
            
            {/* Decorative Element */}
            <div className="border-l-4 border-white pl-6 mb-8">
              <div className="flex items-center text-white">
                <div className="w-12 h-12 flex items-center justify-center mr-4">
                  <i className="ri-time-line text-4xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-lg">Average Response Time</p>
                  <p className="text-2xl font-bold">1.5 Hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <form id="service-request-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {/* Equipment Type */}
                <div className="mb-6">
                  <label htmlFor="equipmentType" className="block text-gray-700 font-semibold mb-2">
                    Equipment Type *
                  </label>
                  <select
                    id="equipmentType"
                    name="equipmentType"
                    required
                    value={formData.equipmentType}
                    onChange={(e) => setFormData({ ...formData, equipmentType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select Equipment Type</option>
                    <option value="Power Wheelchair">Power Wheelchair</option>
                    <option value="Medical Scooter">Medical Scooter</option>
                    <option value="Hospital Bed">Hospital Bed</option>
                    <option value="Lift Chair">Lift Chair</option>
                    <option value="Patient Lift">Patient Lift</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Service Location */}
                <div className="mb-6">
                  <label htmlFor="serviceLocation" className="block text-gray-700 font-semibold mb-2">
                    Service Location *
                  </label>
                  <select
                    id="serviceLocation"
                    name="serviceLocation"
                    required
                    value={formData.serviceLocation}
                    onChange={(e) => setFormData({ ...formData, serviceLocation: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select Your Location</option>
                    <option value="New York City">New York City</option>
                    <option value="Westchester County">Westchester County</option>
                    <option value="New Jersey">New Jersey</option>
                    <option value="Connecticut">Connecticut</option>
                    <option value="Other (within 100 miles of NYC)">Other (within 100 miles of NYC)</option>
                  </select>
                </div>

                {/* Issue Description */}
                <div className="mb-6">
                  <label htmlFor="issueDescription" className="block text-gray-700 font-semibold mb-2">
                    Issue Description *
                  </label>
                  <textarea
                    id="issueDescription"
                    name="issueDescription"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.issueDescription}
                    onChange={(e) => setFormData({ ...formData, issueDescription: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Please describe the issue with your equipment..."
                  ></textarea>
                  <p className="text-gray-500 text-xs mt-1">{formData.issueDescription.length}/500 characters</p>
                </div>

                {/* Contact Method */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-3">
                    Preferred Contact Method *
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={formData.contactMethod === 'phone'}
                        onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
                        className="w-4 h-4 text-[#14B8A6] focus:ring-[#14B8A6] cursor-pointer"
                      />
                      <span className="ml-2 text-gray-700">Phone</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={formData.contactMethod === 'email'}
                        onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
                        className="w-4 h-4 text-[#14B8A6] focus:ring-[#14B8A6] cursor-pointer"
                      />
                      <span className="ml-2 text-gray-700">Email</span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#14B8A6] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0f9b8e] transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Repair Service'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-center font-semibold">
                      <i className="ri-check-line mr-2"></i>
                      Thank you! We'll contact you within 2 hours.
                    </p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-center font-semibold">
                      <i className="ri-error-warning-line mr-2"></i>
                      Something went wrong. Please try again or call us directly.
                    </p>
                  </div>
                )}

                {/* Privacy Note */}
                <p className="text-gray-500 text-xs text-center mt-4">
                  We respect your privacy. Information used for service only.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}